import { useEffect, useState } from "react";
import { convert, Nil } from "cy-util";
import { useStaticCallback } from "./useStaticCallback";
import { useRouteState } from "./useRouteState";

export type MemoryPaginationOptions = {
  pageSize?: number;
  useRouteStore?: boolean;
  name?: string;
};

export function useInMemoryPagination<T>(
  items: T[] | Nil,
  options: MemoryPaginationOptions = {},
) {
  const name = options.name || "page";
  const pageNumberKey = `${name}_pageNumber`;
  const pageSize = options.pageSize || 50;
  const useRouteStorage = options.useRouteStore;

  const reactState = useState<string | undefined>();
  const routeState = useRouteState().useState(pageNumberKey);

  const [pageNumberStr, setPageNumberStr] = useRouteStorage
    ? routeState
    : reactState;

  let pageNumber: number = convert.tryToInt(pageNumberStr)!;
  if (pageNumber == null) {
    pageNumber = 1;
  }

  const setPageNumber = useStaticCallback((pageNumber: number) => {
    const value = pageNumber <= 1 ? undefined : pageNumber.toString();
    setPageNumberStr(value);
  });

  const isLoading = items == null;
  items = items || [];
  const totalItems = items.length || 0;

  const hasPrevious = pageNumber > 1;
  const totalPages = Math.ceil(items.length / pageSize);
  const hasNext = pageNumber < totalPages;
  const start = pageSize * (pageNumber - 1);
  const data = isLoading ? null : items.slice(start, start + pageSize);

  useEffect(() => {
    if (data && data.length === 0 && pageNumber !== 1) {
      setPageNumber(1);
    }
  }, [data, pageNumber, setPageNumber]);

  function goNext() {
    if (hasNext) {
      setPageNumber(pageNumber + 1);
    }
  }

  function goPrevious() {
    if (hasPrevious) {
      setPageNumber(pageNumber - 1);
    }
  }

  return {
    pageNumber,
    hasPrevious,
    hasNext,
    goNext,
    goPrevious,
    data,
    totalPages,
    totalItems,
    paginationProps: {
      onNextPage: goNext,
      onPrevPage: goPrevious,
      totalItems,
      hasNext,
      hasPrevious,
      pageNumber: pageNumber,
      loading: !data,
    },
  };
}
